// Infinite scroll - v4.6.3
//
// Exemple avec toutes les options (toutes facultatives, valeurs par défaut ci-dessous) :
//
//   <infinite-scroll
//     auto-load="true"
//     load-more-text="Voir plus de produits"
//     loading-image="{{ 'loader.gif' | asset_url }}"
//     loading-text="{{ 'general.loading' | t }}"
//     threshold="-100"
//     show-progress="false"
//     product-count-selector=".mobile-facets__count"
//     product-selector=".grid__item"
//     pagination-selector=".pagination-wrapper"
//     append-chunk-size="8"
//     section-rendering="true"
//     content-visibility="true"
//     content-visibility-margin="150%"
//     cache-version="{{ first_card | md5 }}"
//     windowed-cache="true"
//   />
//
// windowed-cache : au-delà du plafond, seules les cartes autour de la position sont gardées, les
// autres deviennent des emplacements remplis au retour. "false" : comportement v3.
//
// cache-version : la grille déjà parcourue est gardée en sessionStorage et réinjectée au
// retour sur la même URL, à la place de celle que le serveur vient de rendre. Sans version,
// un changement de markup (réglage, code, données) reste invisible jusqu'à la fin de la
// session. Toute valeur qui change avec le markup convient ; un cache d'une autre version
// est ignoré et la grille du serveur est gardée. `first_card` est à capturer avant la balise,
// ex. {% capture first_card %}{% render 'card-product', card_product: collection.products.first %}{% endcapture %} :
// seul ce qui change la 1re carte est alors détecté (un changement du snippet l'est toujours).
//
// Les valeurs sont du Liquid comme le reste du template : un asset du thème se passe avec
// {{ 'mon-loader.gif' | asset_url }}, une image de réglage avec {{ settings.loader | image_url }},
// une URL de CDN en dur fonctionne aussi.
//
// Déroulé :
//   1. connectedCallback : options lues, puis initializeState.
//   2. initializeState : hauteur des cartes mesurée (applyCardHeight), grille du cache réinjectée,
//      cartes loin de l'ancre vidées (deferFarCards).
//   3. startRestore : repositionnement sur l'ancre (restoreScrollPosition), puis remplissage
//      des cartes vidées et des emplacements (completeRestore), puis pages manquantes (catchUpToTargetPage).
//   4. Ensuite : page suivante au scroll (loadNextPage), cache réécrit au repos (scheduleHtmlCache)
//      et au départ (pagehide), filtre détecté par l'URL (onGridMutation -> handleFilterChange),
//      position remise au retour par le bfcache (pageshow).

if (!customElements.get("infinite-scroll")) {
  const APPENDING_CLASS = "infinite-scroll-appending";
  const CONTENT_VISIBILITY_CLASS = "infinite-scroll-cv";
  const PLACEHOLDER_CLASS = "infinite-scroll-placeholder";
  const PAGE_ATTR = "data-infinite-scroll-page";
  const SLOT_ATTR = "data-infinite-scroll-slot";
  const NEAR_CLASS = "infinite-scroll-near";
  const CARD_HEIGHT_VAR = "--infinite-scroll-card-height";
  const HOLDER_HEIGHT_VAR = "--infinite-scroll-holder-height";
  const RESTORE_NEAR = 16; // cartes insérées tout de suite de part et d'autre de l'ancre (~2 écrans)
  const RESTORE_CHUNK = 4; // cartes remises par créneau libre ensuite
  // Gestes qui annulent un repositionnement. Pas l'événement scroll : pendant le chargement, le navigateur
  // fait aussi défiler la page (iOS : écrêtage tant que la page n'est pas mise en page), ce n'est pas l'utilisateur.
  const USER_GESTURES = ["wheel", "touchmove", "pointerdown", "keydown"];
  class InfiniteScrollComponent extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 1;
      this.isLoading = false;
      this.hasMorePages = true;
      this.loadedProductsCount = 0;
      this.totalProductsCount = 0;
      this.gridObserver = null;
      this.lastProductsSignature = null;
      this._cacheJob = null;
      this._htmlCacheDisabled = false;
      this._windowedMode = false;
      this._window = null;
      this._disconnected = false;
      this._loadGeneration = 0;
      // Options à modifier selon les préférences
      this.options = {
        // Charge la page suivante au scroll ; false affiche un bouton "voir plus".
        autoLoad: true,
        // Libellé de ce bouton, donc sans effet tant que autoLoad est true. Traduisible en utilisant une variable js initialisée dans theme.liquid par exemple.
        loadMoreText: "Load More Products",
        // Texte affiché pendant un chargement, à la place du visuel s'il est renseigné.
        loadingText: "",
        // Image ou GIF affiché pendant un chargement ; vide, c'est l'animation CSS .infinite-scroll-loading-spinner.
        loadingImageUrl: "",
        // Distance au bas de la grille qui déclenche le chargement en px.
        scrollThreshold: -100,
        // Compteur de produits et barre de progression, en mode bouton uniquement.
        showProgress: false,

        // Le conteneur des cartes produit.
        productsContainer: "#product-grid",
        // Une carte produit à l'intérieur de ce conteneur.
        productSelector: ".grid__item",
        // La pagination du thème, masquée au démarrage.
        paginationSelector: ".pagination-wrapper",
        // Où lire le total de produits ; sert uniquement à la barre de progression.
        productCountSelector: ".mobile-facets__count",

        // Produits insérés à la fois : sans effet sur le products per page, réduit le lag.
        appendChunkSize: 8,
        // Ne demander que la section ; vérifier que sa réponse contient bien la pagination.
        useSectionRendering: false,
        // Allège une grille très longue ; cartes de même hauteur requises (voir README).
        contentVisibility: false,
        // Distance à l'écran où une carte est rendue d'avance, en % de hauteur d'écran ou en px ; plus
        // grand, moins de cartes vides au scroll rapide, mais plus de cartes à rendre.
        contentVisibilityMargin: "150%",
        // Charge sans attendre le 1er visuel des cartes insérées ; requis si contentVisibility.
        eagerAppendedImages: true,
        // Attente maximale des visuels avant de dévoiler un paquet de cartes, en ms.
        revealImageTimeout: 1200,

        // Préfixe des clés sessionStorage du composant.
        storagePrefix: "InfiniteScroll_",
        // Plafond du HTML mis en cache, en caractères (quota ~5,2 M ; Safari refuse plus tôt, sans casse).
        htmlCacheMaxChars: 4500000,
        // Au-delà du plafond, cache fenêtré autour de la position (voir en-tête).
        windowedCache: true,
        // Pages refetchées au maximum au retour sur une collection déjà parcourue.
        maxCatchUpPages: 20,
        // Requêtes simultanées pendant ce rattrapage.
        catchUpConcurrency: 4,
      };
    }

    connectedCallback() {
      if (!window.location.pathname.includes("/collections/")) return;
      if ("scrollRestoration" in history) history.scrollRestoration = "manual";
      [
        ["auto-load", (val) => (this.options.autoLoad = val !== "false")],
        ["load-more-text", (val) => (this.options.loadMoreText = val)],
        ["loading-image", (val) => (this.options.loadingImageUrl = val)],
        ["loading-text", (val) => (this.options.loadingText = val)],
        ["threshold", (val) => (this.options.scrollThreshold = parseInt(val) || 0)],
        ["show-progress", (val) => (this.options.showProgress = val === "true")],
        ["product-count-selector", (val) => (this.options.productCountSelector = val)],
        ["product-selector", (val) => (this.options.productSelector = val)],
        ["pagination-selector", (val) => (this.options.paginationSelector = val)],
        ["append-chunk-size", (val) => (this.options.appendChunkSize = Math.max(1, parseInt(val) || this.options.appendChunkSize))],
        ["section-rendering", (val) => (this.options.useSectionRendering = val === "true")],
        ["content-visibility", (val) => (this.options.contentVisibility = val === "true")],
        // Valeur invalide ignorée : IntersectionObserver la refuserait et la grille ne se rendrait plus d'avance.
        ["content-visibility-margin", (val) => /^\d+(\.\d+)?(px|%)$/.test(val) && (this.options.contentVisibilityMargin = val)],
        ["cache-version", (val) => (this.options.cacheVersion = val)],
        ["windowed-cache", (val) => (this.options.windowedCache = val !== "false")],
      ].forEach(([attributeName, setOption]) =>
        this.hasAttribute(attributeName) && setOption(this.getAttribute(attributeName))
      );
      if (!this.initializeState(true)) return;
      this.setupUI();
      if (this.options.autoLoad) {
        this.boundCheckScroll = () => !this._suppressAutoLoad && this.productsContainer?.getBoundingClientRect().bottom - this.options.scrollThreshold <= window.innerHeight && this.loadNextPage();
        window.addEventListener("scroll", this.boundCheckScroll);
      }
      // Scroll arrêté loin de la zone gardée : on la recentre.
      this.boundRewindow = () => {
        clearTimeout(this._rewindowTimer);
        this._rewindowTimer = setTimeout(() => { if (!this.isLoading && this.windowDrifted(4)) this.scheduleHtmlCache(); }, 1000);
      };
      window.addEventListener("scroll", this.boundRewindow, { passive: true });
      this.boundResize = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => window.innerWidth !== this._cardWidth && this.needsCardHeight() && this.applyCardHeight(), 150);
      };
      window.addEventListener("resize", this.boundResize, { passive: true });
      // Police chargée après la mesure : la hauteur des cartes peut changer.
      this.boundFontsLoaded = () => !this._disconnected && this.needsCardHeight() && this.applyCardHeight();
      document.fonts?.ready.then(this.boundFontsLoaded);
      document.fonts?.addEventListener("loadingdone", this.boundFontsLoaded);
      this.boundSaveOnHide = () => { this.saveAnchor(); this.flushHtmlCache(); };
      window.addEventListener("pagehide", this.boundSaveOnHide);
      this.boundPageShow = (e) => this.handlePageShow(e);
      window.addEventListener("pageshow", this.boundPageShow);
      this.boundSaveOnHidden = () => { if (document.visibilityState === "hidden") { this.saveAnchor(); this.flushHtmlCache(); } };
      document.addEventListener("visibilitychange", this.boundSaveOnHidden);
      this.startRestore(); // HTML caché réinjecté + rattrapage des pages manquantes
      this.observeGridChanges();
    }

    // Page rendue par le bfcache : avec scrollRestoration manuel, WebKit (iOS) ne remet pas la position.
    // Au premier chargement (persisted faux), rien : la restauration est déjà faite.
    handlePageShow(e) {
      if (!e.persisted) return;
      this._restoreDone = false;
      this.restoreScrollPosition();
    }

    initializeState(useCache = false) {
      this.productsContainer = document.querySelector(this.options.productsContainer);
      this.pagination = document.querySelector(this.options.paginationSelector);
      if (!this.productsContainer) return false;
      if (this.options.contentVisibility) this.productsContainer.classList.add(CONTENT_VISIBILITY_CLASS);
      const productCountElement = document.querySelector(this.options.productCountSelector);
      if (productCountElement) {
        const text = productCountElement.textContent.trim();
        const match = text.match(/\d+/);
        this.totalProductsCount = match ? parseInt(match[0]) : 0;
      }
      // Mesurée sur les cartes du serveur, avant l'insertion du cache : la variable posée ensuite ne
      // relance pas le calcul de style de toute la grille restaurée.
      if (this.needsCardHeight()) this.applyCardHeight();
      const cached = useCache && this.cache("get");
      // Ignoré, pas effacé : targetPage et l'ancre survivent, le rattrapage refetch le nouveau markup.
      const staleVersion = this.options.cacheVersion && this.cache("get", "version") !== this.options.cacheVersion;
      if (cached?.url === window.location.href && !staleVersion) {
        // Pas innerHTML : il connecte les nouveaux custom elements avant de déconnecter les anciens (casse Depict).
        const parsed = document.createElement("template");
        parsed.innerHTML = cached.html;
        // Avant l'insertion : le connectedCallback des cartes doit trouver un slider propre.
        this.cleanSwiperTraces(parsed.content);
        this.productsContainer.textContent = "";
        this.productsContainer.append(this.deferFarCards(parsed.content));
        this.currentPage = cached.currentPage;
        this.hasMorePages = cached.hasMorePages;
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
        this._windowedMode = !!this.productsContainer.querySelector("." + PLACEHOLDER_CLASS);
        if (this._windowedMode) {
          const real = this.getProducts().map((el, k) => (el.classList.contains(PLACEHOLDER_CLASS) ? -1 : k)).filter((k) => k >= 0);
          this._window = real.length ? { from: real[0], to: real[real.length - 1] } : null;
        }
      } else {
        this.currentPage = 1;
        this.hasMorePages = this.checkHasMorePages(document);
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
        if (this.options.windowedCache) this.getProducts().forEach((el) => el.setAttribute(PAGE_ATTR, "1"));
      }
      if (!this._cardHeight && this.needsCardHeight()) this.applyCardHeight();
      this.sizePlaceholders();
      // Le conteneur vient d'être re-requêté : l'observateur de l'ancien élément est mort avec lui.
      if (this.gridObserver) this.observeGridChanges();
      return true;
    }

    setupUI() {
      if (!this.pagination) return;
      this.pagination.style.display = "none";
      this.removeLoadingElements();
      // Plus rien à charger : pas de loader, donc pas de place réservée sous la grille (CSS de la boutique).
      if (!this.hasMorePages) return;
      if (!this.options.autoLoad) {
        this.loadMoreWrapper = this.createElementAfter(this.productsContainer, "div", { class: "infinite-scroll-load-more-wrapper" });
        if (this.options.showProgress) {
          this.productCountText = this.createAndAppend(this.loadMoreWrapper, "p", { class: "infinite-scroll-product-count" });
          this.progressBarBackground = this.createAndAppend(this.loadMoreWrapper, "div", { class: "infinite-scroll-progress-bar-background" });
          this.progressBar = this.createAndAppend(this.progressBarBackground, "div", { class: "infinite-scroll-progress-bar" });
        }
        this.loadMoreBtn = this.createAndAppend(this.loadMoreWrapper, "button", { class: "infinite-scroll-load-more", text: this.options.loadMoreText, onclick: () => this.loadNextPage() });
      }
      this.loadingImgContainer = this.createElementAfter(this.loadMoreWrapper || this.productsContainer, "div", {class: "infinite-scroll-loading-image-container"});
      // Un texte l'emporte sur le visuel : il se traduit depuis le Liquid et s'anime en CSS.
      if (this.options.loadingText) this.createAndAppend(this.loadingImgContainer, "p", {class: "infinite-scroll-loading-text", text: this.options.loadingText});
      else if (this.options.loadingImageUrl) this.loadingImg = this.createAndAppend(this.loadingImgContainer, "img", {class: "infinite-scroll-loading-image", src: this.options.loadingImageUrl, alt: "Loading image", style: "display:none;"});
      else this.createAndAppend(this.loadingImgContainer, "span", {class: "infinite-scroll-loading-spinner", role: "img", "aria-label": "Loading"});
      this.updateLoadingState(false);
    }

    getProducts() {
      return this.productsContainer ? Array.from(this.productsContainer.querySelectorAll(this.options.productSelector)) : [];
    }

    rememberTargetPage() {
      this.cache("set", { targetPage: this.currentPage, targetUrl: window.location.href });
    }

    startRestore() {
      const anchorIndex = this.cache("get", "anchorIndex");
      const anchorReady = Number.isFinite(anchorIndex) && anchorIndex >= 0 && anchorIndex < this.loadedProductsCount;
      if (anchorReady) {
        this.restoreScrollPosition();
        this.completeRestore().then(() => this.catchUpToTargetPage());
        return;
      }
      // Ancre hors du cache : ses pages d'abord, le remplissage des emplacements ensuite.
      if (window.InfiniteScrollDebug && Number.isFinite(anchorIndex)) console.log("[infinite-scroll] ancre " + anchorIndex + " hors du cache (" + this.loadedProductsCount + " cartes) : rattrapage d'abord");
      // Un geste pendant l'attente vaut reprise en main : pas de repositionnement ensuite.
      const cancel = () => (this._restoreDone = true);
      USER_GESTURES.forEach((type) => window.addEventListener(type, cancel, { passive: true, once: true }));
      this.catchUpToTargetPage().then(() => {
        USER_GESTURES.forEach((type) => window.removeEventListener(type, cancel));
        this.restoreScrollPosition();
        return this.completeRestore();
      });
    }

    async catchUpToTargetPage() {
      if (this.isLoading) return false;
      if (this.cache("get", "targetUrl") !== window.location.href) return false;
      const target = parseInt(this.cache("get", "targetPage"), 10);
      if (!Number.isFinite(target) || target <= this.currentPage) return false;
      const lastPage = Math.min(target, this.currentPage + this.options.maxCatchUpPages);
      this.isLoading = true; // bloque aussi l'autoload au scroll (loadNextPage sort si isLoading)
      const gen = this._loadGeneration;
      this.updateLoadingState(true);
      try {
        // En parallèle, mais insérées dans l'ordre ; la première page en échec arrête tout.
        const inFlight = new Map();
        let nextToFetch = this.currentPage + 1;
        for (let page = this.currentPage + 1; page <= lastPage && this.hasMorePages && !this.isStale(gen); page++) {
          while (nextToFetch <= lastPage && inFlight.size < this.options.catchUpConcurrency) {
            inFlight.set(nextToFetch, this.fetchPageDocument(nextToFetch).catch(() => null));
            nextToFetch++;
          }
          const loaded = await inFlight.get(page);
          inFlight.delete(page);
          if (!loaded || this.isStale(gen) || !(await this.insertPage(page, loaded, gen, true))) break;
        }
        if (!this.hasMorePages) this.handleEndOfResults();
        if (lastPage === target) this.rememberTargetPage();
        this.scheduleHtmlCache();
      } catch {
      } finally {
        this.revealPending();
        if (gen === this._loadGeneration) { // même verrou que loadNextPage
          this.isLoading = false;
          this.updateLoadingState(false);
        }
      }
      return true;
    }

    // Après le repositionnement : cartes différées remises, pages des emplacements rechargées.
    // Sans le verrou isLoading : l'infinite scroll reste actif, et passe avant (yieldToLoads).
    async completeRestore() {
      if (this._restoring || (!this._deferred?.size && !this.productsContainer.querySelector("." + PLACEHOLDER_CLASS))) return;
      this._restoring = true;
      const gen = this._loadGeneration;
      this.watchNearHolders();
      try {
        await Promise.all([this.hydrateDeferred(gen), this.fillPlaceholders(gen)]);
      } catch {
      } finally {
        if (this.holderObserver) this.holderObserver.disconnect();
        if (gen === this._loadGeneration) {
          this._restoring = false;
          this.loadedProductsCount = this.getProducts().length;
          this.lastProductsSignature = this.getProductsSignature();
          this.scheduleHtmlCache(); // sauvegardes sautées pendant le remplissage
        }
      }
    }

    // Emplacements proches de l'écran : remplis en premier, où que l'utilisateur soit allé.
    watchNearHolders() {
      this._nearHolders = new Set();
      if (!window.IntersectionObserver) return;
      if (this.holderObserver) this.holderObserver.disconnect();
      this.holderObserver = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? this._nearHolders.add(e.target) : this._nearHolders.delete(e.target)));
      }, { rootMargin: "100% 0px" });
      this.productsContainer.querySelectorAll("." + PLACEHOLDER_CLASS).forEach((el) => this.holderObserver.observe(el));
    }

    forgetHolder(el) {
      this.holderObserver?.unobserve(el);
      this._nearHolders?.delete(el);
    }

    async yieldToLoads(gen) {
      while (this.isLoading && !this.isStale(gen)) await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Cartes du cache loin de l'ancre : vidées, remplies ensuite par hydrateDeferred. Vider plutôt
    // que remplacer : la liste des enfants de la grille ne bouge pas, Depict ne recalcule rien.
    deferFarCards(fragment) {
      this._deferred = new Map();
      const anchor = this.cache("get", "anchorIndex");
      if (!Number.isFinite(anchor)) return fragment;
      const products = Array.from(fragment.children).filter((el) => el.matches(this.options.productSelector));
      // Ancre hors du cache : la page reste en haut pendant le rattrapage.
      const center = anchor < products.length ? anchor : 0;
      products.forEach((el, k) => {
        if (Math.abs(k - center) <= RESTORE_NEAR || el.classList.contains(PLACEHOLDER_CLASS)) return;
        const depictId = el.querySelector("[data-depict-product-id]")?.getAttribute("data-depict-product-id");
        const content = el.ownerDocument.createDocumentFragment(); // document inerte : rien ne se charge
        content.append(...el.childNodes);
        this._deferred.set(el, { content, style: el.getAttribute("style") });
        el.classList.add(PLACEHOLDER_CLASS);
        if (depictId) el.setAttribute("data-depict-product-id", depictId);
      });
      return fragment;
    }

    // La mesure force une mise en page complète (~400 ms sur une grille lourde) : seulement si elle sert.
    needsCardHeight() {
      return this.options.contentVisibility || !!this.productsContainer.querySelector("." + PLACEHOLDER_CLASS);
    }

    // Cartes toutes de même hauteur : rendues ou non (content-visibility), même place, barre de scroll fixe.
    applyCardHeight() {
      const height = this.cardHeight();
      this._cardWidth = window.innerWidth;
      if (!height) return;
      this._cardHeight = height;
      const style = this.productsContainer.style;
      // Inchangée sur cette grille : ne pas relancer le calcul de style de toute la grille.
      if (style.getPropertyValue(CARD_HEIGHT_VAR) === height.content + "px" && style.getPropertyValue(HOLDER_HEIGHT_VAR) === height.holder + "px") return;
      style.setProperty(CARD_HEIGHT_VAR, height.content + "px");
      style.setProperty(HOLDER_HEIGHT_VAR, height.holder + "px");
    }

    sizePlaceholders() {
      if (!this._cardHeight) return;
      const style = this.placeholderStyle(this._cardHeight);
      this.productsContainer.querySelectorAll("." + PLACEHOLDER_CLASS).forEach((el) => this.keepOrder(el, () => el.setAttribute("style", style)));
    }

    // Hauteur la plus fréquente sur 8 cartes : une carte différente ne fausse pas la mesure.
    // content : celle de contain-intrinsic-height ; holder : le `height` d'un emplacement, selon le box-sizing du thème
    // (le changer élargirait aussi l'emplacement). Hors écran, content-visibility renvoie l'estimation CSS et la grille
    // étire la carte à sa rangée : cartes rendues, non étirées et sans notre min-height le temps de la mesure.
    cardHeight() {
      const sample = [];
      for (const el of this.getProducts()) {
        if (!el.classList.contains(PLACEHOLDER_CLASS) && el.querySelector('a[href*="/products/"]') && sample.push(el) === 8) break;
      }
      if (!sample.length) return null;
      const styles = sample.map((el) => el.getAttribute("style"));
      sample.forEach((el) => {
        el.style.contentVisibility = "visible";
        el.style.alignSelf = "start";
        el.style.setProperty(HOLDER_HEIGHT_VAR, "0px"); // sinon, après un redimensionnement, on relirait l'ancienne hauteur
      });
      const outers = sample.map((el) => el.getBoundingClientRect().height);
      const counts = new Map();
      outers.forEach((h) => counts.set(h, (counts.get(h) || 0) + 1));
      const outer = [...counts].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0][0];
      const cs = getComputedStyle(sample[outers.indexOf(outer)]);
      const content = outer - ["borderTopWidth", "borderBottomWidth", "paddingTop", "paddingBottom"].reduce((n, p) => n + parseFloat(cs[p]), 0);
      const holder = cs.boxSizing === "border-box" ? outer : content;
      sample.forEach((el, i) => (styles[i] === null ? el.removeAttribute("style") : el.setAttribute("style", styles[i])));
      return { content, holder };
    }

    placeholderStyle(height) {
      return `height:var(${HOLDER_HEIGHT_VAR},${height.holder}px)`;
    }

    async hydrateDeferred(gen) {
      const deferred = this._deferred;
      if (!deferred?.size) return;
      const anchor = this.cache("get", "anchorIndex") || 0;
      const order = this.getProducts().map((el, k) => [el, Math.abs(k - anchor)]).filter(([el]) => deferred.has(el)).sort((a, b) => a[1] - b[1]).map(([el]) => el);
      let deadline = null, next = 0;
      for (let i = 0; deferred.size; i++) {
        if (i && i % RESTORE_CHUNK === 0 && !(deadline?.timeRemaining() > 8)) {
          await this.yieldToLoads(gen);
          deadline = await this.idleSlice();
          if (this.isStale(gen)) return;
        }
        // Proche de l'écran d'abord, sinon par distance à l'ancre.
        let el = [...this._nearHolders].find((h) => deferred.has(h));
        while (!el && next < order.length) {
          const candidate = order[next++];
          if (deferred.has(candidate)) el = candidate;
        }
        if (!el) return;
        const { content, style } = deferred.get(el);
        this.keepOrder(el, () => (style === null ? el.removeAttribute("style") : el.setAttribute("style", style)));
        el.classList.remove(PLACEHOLDER_CLASS);
        el.append(content);
        deferred.delete(el);
        this.forgetHolder(el);
      }
    }

    // Résout avec le délai libre restant : on enchaîne les paquets tant qu'il en reste.
    idleSlice() {
      return new Promise((resolve) => (window.requestIdleCallback ? requestIdleCallback(resolve, { timeout: 100 }) : setTimeout(() => resolve(null), 16)));
    }

    async fillPlaceholders(gen) {
      // [SLOT_ATTR] : emplacements du cache seulement, pas les cartes différées (déjà en mémoire).
      const holders = this.productsContainer.querySelectorAll(`.${PLACEHOLDER_CLASS}[${SLOT_ATTR}]`);
      if (!holders.length) return;
      const pages = [...new Set(Array.from(holders, (el) => parseInt(el.getAttribute(PAGE_ATTR), 10)))].filter(Number.isFinite);
      const anchor = this.getProducts()[this.cache("get", "anchorIndex")];
      const center = parseInt(anchor?.getAttribute(PAGE_ATTR), 10) || pages[0];
      pages.sort((a, b) => Math.abs(a - center) - Math.abs(b - center)); // le plus proche d'abord
      const pending = new Set(pages);
      // Page d'un emplacement proche de l'écran d'abord, sinon par distance à l'ancre.
      const pickPage = () => {
        for (const h of this._nearHolders) {
          const page = parseInt(h.getAttribute(PAGE_ATTR), 10);
          if (h.hasAttribute(SLOT_ATTR) && pending.has(page)) return page;
        }
        return pages.find((page) => pending.has(page));
      };
      const worker = async () => {
        while (pending.size && !this.isStale(gen)) {
          await this.yieldToLoads(gen);
          const page = pickPage();
          if (page === undefined) return;
          pending.delete(page);
          const loaded = await this.fetchPageDocument(page).catch(() => null);
          if (loaded && !this.isStale(gen)) await this.replacePlaceholders(page, loaded.container.children, gen);
        }
      };
      await Promise.all(Array.from({ length: Math.min(this.options.catchUpConcurrency, pages.length) }, worker));
    }

    async replacePlaceholders(page, children, gen) {
      const holders = Array.from(this.productsContainer.querySelectorAll(`.${PLACEHOLDER_CLASS}[${PAGE_ATTR}="${page}"][${SLOT_ATTR}]`));
      const cards = Array.from(children).filter((el) => el.matches(this.options.productSelector));
      let deadline = null;
      for (let i = 0; i < holders.length; i++) {
        if (i && i % RESTORE_CHUNK === 0 && !(deadline?.timeRemaining() > 8)) {
          await this.yieldToLoads(gen);
          deadline = await this.idleSlice();
          if (this.isStale(gen)) return;
        }
        // Rang dans la page : une page peut chevaucher la zone gardée.
        const source = cards[parseInt(holders[i].getAttribute(SLOT_ATTR), 10)];
        const holder = holders[i];
        if (!source) { this.forgetHolder(holder); holder.remove(); continue; } // la collection a raccourci
        // Rempli sur place, comme hydrateDeferred : pas de recalcul Depict.
        this.keepOrder(holder, () => {
          Array.from(holder.attributes).forEach((a) => holder.removeAttribute(a.name));
          Array.from(source.attributes).forEach((a) => holder.setAttribute(a.name, a.value));
        });
        holder.setAttribute(PAGE_ATTR, page);
        holder.append(...source.childNodes);
        this.forgetHolder(holder);
      }
    }

    // Depict place les cartes avec --order : à garder si une réécriture du style l'efface.
    keepOrder(el, rewrite) {
      const order = el.style.getPropertyValue("--order");
      rewrite();
      if (order && !el.style.getPropertyValue("--order")) el.style.setProperty("--order", order);
    }

    firstVisibleIndex(products) {
      for (let i = 0; i < products.length; i++) {
        const rect = products[i].getBoundingClientRect();
        if (rect.bottom > 0) return { index: i, offset: Math.round(rect.top) };
      }
      return { index: -1, offset: 0 };
    }

    saveAnchor() {
      // On mémorise quel produit était en haut, pas un pixel : un pixel dérive au chargement
      // des images situées au-dessus.
      const { index: anchorIndex, offset: anchorOffset } = this.firstVisibleIndex(this.getProducts());
      const anchor = { scrollUrl: window.location.href, anchorIndex, anchorOffset };
      if (this.cache("set", anchor) === true) return;
      // Stockage plein : la position passe avant le HTML (sans lui, le retour rattrape les pages par le réseau).
      this.cache("set", "html", "");
      if (this.cache("set", anchor) !== true) console.warn("[infinite-scroll] sessionStorage plein : position de scroll non enregistrée");
    }

    restoreScrollPosition() {
      const savedUrl = this.cache("get", "scrollUrl");
      if (savedUrl !== window.location.href) return; // pas la même collection -> on ne touche à rien
      const anchorIndex = this.cache("get", "anchorIndex");
      const anchorOffset = this.cache("get", "anchorOffset") || 0;
      // Uniquement sur un produit présent : une position en pixels mémorisée dépasserait la
      // hauteur d'une grille plus courte, et le scrollTo écrêté atterrirait tout en bas.
      if (!Number.isFinite(anchorIndex) || anchorIndex < 0) return;
      if (!this.getProducts()[anchorIndex]) return;
      // Une seule fois par affichage : un second appel ramènerait l'utilisateur en arrière.
      if (this._restoreDone) return;
      this._restoreDone = true;

      this._suppressAutoLoad = true;
      const gen = this._loadGeneration;
      let stopped = false, reached = false, stopTimer = null;
      const stop = () => {
        if (stopped) return;
        stopped = true;
        this._suppressAutoLoad = false;
        USER_GESTURES.forEach((type) => window.removeEventListener(type, stop));
        clearTimeout(stopTimer);
      };

      const computeTarget = () => {
        const anchor = this.getProducts()[anchorIndex];
        return anchor ? anchor.getBoundingClientRect().top + window.scrollY - anchorOffset : null;
      };
      const apply = () => {
        const raw = computeTarget();
        if (raw === null) return stop();
        const target = Math.max(0, raw);
        // Ne scroller que si la page peut atteindre la cible, sinon le navigateur écrête vers le bas.
        if (document.documentElement.scrollHeight - window.innerHeight < target - 4) return;
        // `instant` : un `scroll-behavior: smooth` du thème ferait sinon défiler la page depuis le haut.
        if (Math.abs(window.scrollY - target) > 2) window.scrollTo({ top: target, behavior: "instant" });
        // 2,5 s de correction encore, comptées une fois la cible atteinte et la page chargée.
        if (!reached && document.readyState === "complete") {
          reached = true;
          clearTimeout(stopTimer);
          stopTimer = setTimeout(stop, 2500);
        }
      };
      // À chaque frame tant que c'est actif : la page grandit pendant le chargement, et le navigateur
      // peut la déplacer lui-même. Arrêt aussi sur filtre ou démontage : l'ancre visait l'ancienne grille.
      const tick = () => {
        if (this.isStale(gen)) stop();
        if (stopped) return;
        apply();
        requestAnimationFrame(tick);
      };
      USER_GESTURES.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));
      stopTimer = setTimeout(stop, 10000); // borne dure si la cible ne devient jamais atteignable
      tick();
    }

    saveHtmlCache() {
      if (this._htmlCacheDisabled || !this.productsContainer) return;
      // Cartes vidées en cours de remplissage : seul le cache fenêtré sait les décrire (emplacements).
      const deferred = this._deferred?.size > 0;
      if (deferred && !this.options.windowedCache) return;
      this.revealPending(); // jamais de classe de masquage dans le snapshot
      this.restoreLazyLoading(); // ni 900 images `eager` : voir restoreLazyLoading
      let html = this._windowedMode || deferred ? null : this.productsContainer.innerHTML;
      if (this.options.windowedCache && (html === null || html.length > this.cacheBudget())) {
        const built = this.buildWindowedHtml();
        this._cannotWindow = built === null;
        if (built !== null) { html = built; this._windowedMode = true; }
      }
      if (html === null) {
        if (deferred) return;
        html = this.productsContainer.innerHTML;
      }
      // ORDRE CRITIQUE : le HTML d'abord, les métadonnées seulement si son écriture a réussi,
      // sinon on stocke currentPage=10 avec le HTML de la page 5 et des pages sont sautées.
      if (html.length > this.options.htmlCacheMaxChars || this.cache("set", "html", html) !== true) {
        // Quota plus bas que prévu (Safari) : zone gardée deux fois plus petite, plutôt que figer.
        if (this.options.windowedCache && !this._cannotWindow && this.cacheBudget() > this.options.htmlCacheMaxChars / 8) {
          this._cacheBudget = Math.floor(Math.min(this.cacheBudget(), html.length) / 2);
          return this.saveHtmlCache();
        }
        this._htmlCacheDisabled = true;
        console.warn("[infinite-scroll] sessionStorage plein : grille plus mise en cache, le retour rechargera les pages");
        return;
      }
      // JSON.stringify : sans lui, la relecture par JSON.parse rendrait "1.0" en 1.
      this.cache("set", { currentPage: this.currentPage, hasMorePages: this.hasMorePages, url: window.location.href, version: JSON.stringify(this.options.cacheVersion ?? "") });
    }

    // null si les cartes n'ont pas de numéro de page (cache v3).
    buildWindowedHtml() {
      const children = Array.from(this.productsContainer.children || []);
      // Clones Depict exclus : Depict les recrée.
      const isProduct = (el) => el.matches(this.options.productSelector) && !el.hasAttribute("data-depict-clone");
      const products = children.filter(isProduct);
      if (!products.some((el) => el.hasAttribute(PAGE_ATTR))) return null;
      const height = this._cardHeight || this.cardHeight() || { holder: 0 };
      const slots = {};
      const placeholders = products.map((el) => {
        const page = el.getAttribute(PAGE_ATTR);
        if (page === null) return "";
        slots[page] = (slots[page] || 0) + 1;
        return this.placeholderHtml(el, page, slots[page] - 1, height);
      });
      const productIndex = new Map(products.map((el, k) => [el, k]));
      const parts = children.map((el) => (productIndex.has(el) ? placeholders[productIndex.get(el)] : el.matches(this.options.productSelector) ? "" : el.outerHTML));
      let total = parts.reduce((n, s) => n + s.length, 0);
      const anchor = Math.max(0, this.firstVisibleIndex(products).index);
      let from = anchor, to = anchor - 1;
      // Zone élargie alternativement dessous / dessus, jusqu'au plafond.
      for (let below = true; from > 0 || to < products.length - 1; below = !below) {
        const k = (below && to < products.length - 1) || from === 0 ? to + 1 : from - 1;
        const i = children.indexOf(products[k]);
        const full = this._deferred?.has(products[k]) ? parts[i] : products[k].outerHTML; // vidée : reste un emplacement
        if (total - parts[i].length + full.length > this.cacheBudget()) break;
        total += full.length - parts[i].length;
        parts[i] = full;
        if (k > to) to = k; else from = k;
      }
      this._window = { from, to };
      return parts.join("");
    }

    cacheBudget() {
      return Math.min(this._cacheBudget || Infinity, this.options.htmlCacheMaxChars);
    }

    placeholderHtml(el, page, slot, height) {
      const escape = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
      const tag = el.tagName.toLowerCase();
      const cls = (el.getAttribute("class") || "").replace(APPENDING_CLASS, "").replace(PLACEHOLDER_CLASS, "").replace(NEAR_CLASS, "").trim();
      // Recopié : Depict ordonne la grille par cet identifiant.
      const depictEl = el.matches("[data-depict-product-id]") ? el : el.querySelector("[data-depict-product-id]");
      const depictId = depictEl?.getAttribute("data-depict-product-id");
      return `<${tag} class="${escape(cls)} ${PLACEHOLDER_CLASS}" ${PAGE_ATTR}="${escape(page)}" ${SLOT_ATTR}="${slot}"` +
        (depictId ? ` data-depict-product-id="${escape(depictId)}"` : "") +
        ` style="${this.placeholderStyle(height)}"></${tag}>`;
    }

    // Position hors de la zone gardée (margin = 4 : dans son quart extérieur).
    windowDrifted(margin) {
      if (!this._window) return false;
      const { from, to } = this._window;
      const edge = margin ? (to - from) / margin : 0;
      const k = this.firstVisibleIndex(this.getProducts()).index;
      return k >= 0 && (k < from + edge || k > to - edge);
    }

    scheduleHtmlCache() {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
      this.cancelHtmlCache();
      this._cacheJob = idle(() => { this._cacheJob = null; this.saveHtmlCache(); });
    }

    flushHtmlCache() {
      // Aussi si l'utilisateur part hors de la zone gardée.
      if (!this._cacheJob && !this.windowDrifted(0)) return;
      this.cancelHtmlCache();
      this.saveHtmlCache();
    }

    cancelHtmlCache() {
      if (!this._cacheJob) return;
      (window.cancelIdleCallback || clearTimeout)(this._cacheJob);
      this._cacheJob = null;
    }

    createElementAfter(ref, tag, attrs) {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([key, value]) => (key === "text" ? (el.textContent = value) : key === "onclick" ? el.addEventListener("click", value) : el.setAttribute(key, value)));
      ref.insertAdjacentElement("afterend", el);
      return el;
    }

    createAndAppend(parent, tag, attrs) {
      const el = this.createElementAfter(parent, tag, attrs);
      parent.appendChild(el);
      return el;
    }

    removeLoadingElements() {
      document.querySelectorAll(".infinite-scroll-load-more-wrapper, .infinite-scroll-load-more, .infinite-scroll-loading-image-container, .infinite-scroll-loading-image").forEach((el) => el.remove());
      this.loadMoreWrapper = this.loadMoreBtn = this.loadingImgContainer = this.loadingImg = null;
    }

    yieldToBrowser() {
      // rAF ne conviendrait pas : son callback s'exécute avant le rendu, donc il bloque la frame.
      if (window.scheduler && window.scheduler.yield) return window.scheduler.yield();
      return new Promise(function (resolve) { setTimeout(resolve, 0); });
    }

    isStale(gen) {
      // Caduc si démonté, si un filtre est passé (génération), ou si la grille est détachée.
      return this._disconnected || gen !== this._loadGeneration || !this.productsContainer.isConnected;
    }

    async appendInChunks(children, gen, catchUp = false, page = null) {
      const nodes = Array.from(children);
      const size = this.options.appendChunkSize;
      // Un DocumentFragment ne marcherait pas : le connectedCallback ne s'y déclenche pas, tout
      // le coût d'init se reconcentrerait sur l'insertion unique.
      const promoted = [];
      for (let i = 0; i < nodes.length; i += size) {
        const end = Math.min(i + size, nodes.length);
        for (let j = i; j < end; j++) {
          const card = nodes[j].cloneNode(true);
          card.classList.add(APPENDING_CLASS);
          if (page !== null && this.options.windowedCache && card.matches(this.options.productSelector)) card.setAttribute(PAGE_ATTR, page);
          // Rattrapage : cartes hors écran, ni priorité ni attente.
          const img = !catchUp && this.promoteFirstImage(card);
          if (img) promoted.push(img);
          this.productsContainer.appendChild(card);
        }
        if (end >= nodes.length) break;
        await this.yieldToBrowser();
        if (this.isStale(gen)) { this.revealPending(); return false; }
      }
      if (!catchUp) await this.waitForImages(promoted, this.options.revealImageTimeout);
      this.revealPending();
      return !this.isStale(gen);
    }

    promoteFirstImage(card) {
      if (!this.options.eagerAppendedImages) return null;
      // La 1re image de la carte, pas la 1re encore lazy : sinon chaque retour dans la zone en lancerait une de plus.
      const img = card.querySelector("img");
      if (img?.getAttribute("loading") === "lazy") img.setAttribute("loading", "eager");
      return img;
    }

    waitForImages(imgs, timeoutMs) {
      const pending = imgs.filter((img) => img && !img.complete);
      if (!pending.length) return Promise.resolve();
      return Promise.race([
        Promise.all(pending.map((img) => new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true }); // une image cassée ne bloque pas
        }))),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]);
    }

    restoreLazyLoading() {
      if (!this.options.eagerAppendedImages || !this.productsContainer) return;
      // Pas d'eager dans le snapshot : la grille entière est réinjectée d'un coup au retour.
      const imgs = this.productsContainer.querySelectorAll("img[loading='eager']");
      for (let i = 0; i < imgs.length; i++) imgs[i].setAttribute("loading", "lazy");
    }

    cleanSwiperTraces(root = this.productsContainer) {
      // Un slider initialisé laisse ses classes d'état dans le HTML mis en cache, et son init
      // écarte ce qui porte swiper-initialized : sans nettoyage la carte revient inerte.
      const GARDER = /^(swiper-wrapper|swiper-slide|swiper-button-next|swiper-button-prev|swiper-scrollbar)$/;
      const inits = root.querySelectorAll(".swiper-initialized");
      for (let i = 0; i < inits.length; i++) {
        const noeuds = [inits[i]].concat(Array.prototype.slice.call(inits[i].querySelectorAll("*")));
        for (let j = 0; j < noeuds.length; j++) {
          const n = noeuds[j];
          for (let k = n.classList.length - 1; k >= 0; k--) {
            const c = n.classList[k];
            if (c.indexOf("swiper-") === 0 && !GARDER.test(c)) n.classList.remove(c);
          }
          if (n.classList.contains("swiper-wrapper") || n.classList.contains("swiper-slide")) {
            n.removeAttribute("style");
          }
        }
      }
      return inits.length;
    }

    revealPending() {
      if (!this.productsContainer) return 0;
      // Aussi avant chaque écriture du cache : une classe de masquage sérialisée ferait revenir
      // une grille de produits invisibles.
      const marked = this.productsContainer.querySelectorAll("." + APPENDING_CLASS);
      for (let i = 0; i < marked.length; i++) marked[i].classList.remove(APPENDING_CLASS);
      return marked.length;
    }

    async fetchDocument(pageNumber, useSection) {
      const startUrl = window.location.href;
      const url = new URL(startUrl);
      url.searchParams.set("page", pageNumber);
      const sectionId = useSection && this.productsContainer.getAttribute("data-id");
      if (sectionId) url.searchParams.set("section_id", sectionId);
      const gen = this._loadGeneration;
      const t0 = performance.now();
      const res = await fetch(url.toString(), { credentials: "same-origin" });
      if (!res.ok) return null;
      const html = await res.text();
      if (this.isStale(gen) || window.location.href !== startUrl) return null;
      const t1 = performance.now();
      const doc = new DOMParser().parseFromString(html, "text/html");
      return { doc, bytes: html.length, network: t1 - t0, parse: performance.now() - t1, section: !!sectionId };
    }

    async fetchPageDocument(pageNumber) {
      let res = await this.fetchDocument(pageNumber, this.options.useSectionRendering);
      if (!res) return null;
      let container = res.doc.querySelector(this.options.productsContainer);
      if (!container && res.section) {
        this.options.useSectionRendering = false;
        res = await this.fetchDocument(pageNumber, false);
        if (!res) return null;
        container = res.doc.querySelector(this.options.productsContainer);
      }
      return container?.children.length ? { res, container } : null;
    }

    async fetchPage(pageNumber) {
      const gen = this._loadGeneration;
      const loaded = await this.fetchPageDocument(pageNumber);
      return !!loaded && this.insertPage(pageNumber, loaded, gen);
    }

    async insertPage(pageNumber, { res, container }, gen, catchUp = false) {
      const t2 = performance.now();
      if (!(await this.appendInChunks(container.children, gen, catchUp, pageNumber))) return false;
      if (window.InfiniteScrollDebug) {
        console.log(
          "[infinite-scroll] page " + pageNumber + (res.section ? " (section)" : " (page complete)") +
          " : reseau " + Math.round(res.network) + "ms" +
          ", parse " + Math.round(res.parse) + "ms (" + Math.round(res.bytes / 1024) + "Ko)" +
          ", insertion " + Math.round(performance.now() - t2) + "ms par paquets de " + this.options.appendChunkSize
        );
      }
      this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      this.currentPage = pageNumber;
      this.hasMorePages = this.checkHasMorePages(res.doc);
      this.lastProductsSignature = this.getProductsSignature();
      return true;
    }

    async loadNextPage() {
      // Verrou posé avant le premier await : deux scrolls dans la même tâche ne passent pas.
      if (this.isLoading || !this.hasMorePages) return;
      this.isLoading = true;
      const gen = this._loadGeneration;
      this.updateLoadingState(true);
      try {
        if (!(await this.fetchPage(this.currentPage + 1))) return this.handleEndOfResults();
        if (!this.hasMorePages) this.handleEndOfResults();
        this.rememberTargetPage(); // avant le HTML : minuscule, donc jamais refusé par le quota
        this.scheduleHtmlCache();
      } catch {
        this.handleEndOfResults();
      } finally {
        this.revealPending(); // filet : une exception ne doit pas laisser de carte invisible
        if (gen === this._loadGeneration) {
          this.isLoading = false;
          this.updateLoadingState(false);
        }
      }
    }

    checkHasMorePages(doc) {
      const container = doc.querySelector(this.options.productsContainer);
      return container?.children.length > 0 && !!doc.querySelector(`a[href*="page=${this.currentPage + 1}"], .pagination a[rel="next"], .pagination .next:not(.disabled)`);
    }

    updateLoadingState(isLoading) {
      if (this.loadMoreBtn) this.loadMoreBtn.disabled = isLoading;
      if (this.loadMoreWrapper) this.loadMoreWrapper.style.display = isLoading ? "none" : "flex";
      if (this.loadingImgContainer) this.loadingImgContainer.style.display = isLoading ? "flex" : "none";
      if (this.loadingImg) this.loadingImg.style.display = isLoading ? "block" : "none";
      if (this.productCountText && this.totalProductsCount > 0) this.productCountText.textContent = `${this.loadedProductsCount} products loaded out of ${this.totalProductsCount}`;
      if (this.progressBar && this.totalProductsCount > 0) this.progressBar.style.width = `${(this.loadedProductsCount / this.totalProductsCount) * 100}%`;
    }

    handleEndOfResults() {
      this.hasMorePages = false;
      this.removeLoadingElements();
    }

    cache(action, key, value) {
      const formatKey = (s) => this.options.storagePrefix + s;
      const serialize = (val) => typeof val === "object" ? JSON.stringify(val) : String(val);
      const deserialize = (val) => {
        try {
          return val ? JSON.parse(val) : null;
        } catch {
          return val;
        }
      };
      try {
        if (action === "get") {
          if (!key) {
            const vals = Object.fromEntries(["url", "html", "hasMorePages", "currentPage", "scrollUrl"].map((k) => [k, deserialize(sessionStorage.getItem(formatKey(k)))]));
            return vals.url && vals.html ? vals : null;
          }
          return deserialize(sessionStorage.getItem(formatKey(key)));
        }
        if (action === "set") {
          if (typeof key === "object") Object.entries(key).forEach(([k, val]) => sessionStorage.setItem(formatKey(k), serialize(val)));
          else sessionStorage.setItem(formatKey(key), serialize(value));
          return true; // écriture confirmée : saveHtmlCache y détecte le quota
        }
        if (action === "clear") Object.keys(sessionStorage).filter((k) => k.startsWith(this.options.storagePrefix)).forEach((k) => sessionStorage.removeItem(k));
      } catch {
        if (action === "set") return false;
      }
      return null;
    }

    observeGridChanges() {
      if (!this.productsContainer) return;
      if (this.gridObserver) this.gridObserver.disconnect();
      this.lastProductsSignature = this.getProductsSignature();
      this.lastGridUrl = window.location.href;
      this.gridObserver = new MutationObserver((records) => { this.watchNearCards(records); this.onGridMutation(); });
      this.gridObserver.observe(this.productsContainer, { childList: true });
      this.watchNearCards();
    }

    // Cartes rendues d'avance (content-visibility attend ~½ écran), et leur 1re image lancée : une image
    // lazy attend sinon le seuil du navigateur (~1 à 2 écrans), rendre plus tôt ne suffirait pas.
    watchNearCards(records) {
      if (!this.options.contentVisibility || !window.IntersectionObserver) return;
      if (!records) {
        if (this.nearObserver) this.nearObserver.disconnect();
        this.nearObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle(NEAR_CLASS, entry.isIntersecting);
            if (entry.isIntersecting) this.promoteFirstImage(entry.target);
          });
        }, { rootMargin: this.options.contentVisibilityMargin + " 0px" });
        // Sans attendre l'observer (asynchrone) : une carte visible sautée une frame décale la page (CLS).
        const margin = parseFloat(this.options.contentVisibilityMargin) * (this.options.contentVisibilityMargin.endsWith("%") ? window.innerHeight / 100 : 1);
        const products = this.getProducts();
        const near = products.filter((el) => { const r = el.getBoundingClientRect(); return r.bottom > -margin && r.top < window.innerHeight + margin; });
        near.forEach((el) => el.classList.add(NEAR_CLASS));
        products.forEach((el) => this.nearObserver.observe(el));
        return;
      }
      records.forEach((r) => r.addedNodes.forEach((n) => n.nodeType === 1 && n.matches(this.options.productSelector) && this.nearObserver.observe(n)));
    }

    onGridMutation() {
      if (this.isLoading) return;
      if (this.getProductsSignature() === this.lastProductsSignature) return; // bruit tiers
      // Un filtre ou un tri change toujours l'URL ; sans ça, c'est un script tiers (ex. clone Depict).
      if (window.location.href !== this.lastGridUrl && window.location.pathname.includes("/collections/")) this.handleFilterChange();
      this.lastProductsSignature = this.getProductsSignature(); // grille fraîche après reinit
      this.lastGridUrl = window.location.href;
    }

    getProductsSignature() {
      const products = this.productsContainer ? this.productsContainer.querySelectorAll(this.options.productSelector) : [];
      if (!products.length) return "0";
      const idOf = (el) => (el.querySelector('a[href*="/products/"]')?.getAttribute("href") || el.getAttribute("data-product-id") || el.id || "");
      return products.length + "|" + idOf(products[0]) + "|" + idOf(products[products.length - 1]);
    }

    handleFilterChange() {
      this.cancelHtmlCache(); // aucune écriture en attente ne doit atterrir après le clear
      this.cache("clear"); // efface aussi la position mémorisée
      this._htmlCacheDisabled = false; // grille repartie de zéro : le budget sessionStorage est libre
      this._cacheBudget = 0;
      this._windowedMode = false;
      this._window = null;
      this._deferred = null;
      this._restoring = false;
      this._loadGeneration++; // invalide tout chargement en vol : il visait l'ancienne grille
      this.isLoading = false;
      this.initializeState(false);
      this.setupUI();
    }

    disconnectedCallback() {
      this._disconnected = true; // stoppe un rattrapage en cours (voir isStale)
      if (this.boundCheckScroll) window.removeEventListener("scroll", this.boundCheckScroll);
      if (this.boundRewindow) window.removeEventListener("scroll", this.boundRewindow);
      clearTimeout(this._rewindowTimer);
      if (this.boundResize) window.removeEventListener("resize", this.boundResize);
      if (this.boundFontsLoaded) document.fonts?.removeEventListener("loadingdone", this.boundFontsLoaded);
      clearTimeout(this._resizeTimer);
      if (this.boundSaveOnHide) window.removeEventListener("pagehide", this.boundSaveOnHide);
      if (this.boundPageShow) window.removeEventListener("pageshow", this.boundPageShow);
      if (this.boundSaveOnHidden) document.removeEventListener("visibilitychange", this.boundSaveOnHidden);
      this.cancelHtmlCache();
      if (this.gridObserver) this.gridObserver.disconnect();
      if (this.nearObserver) this.nearObserver.disconnect();
      if (this.holderObserver) this.holderObserver.disconnect();
    }
  }
  customElements.define("infinite-scroll", InfiniteScrollComponent);
}
